<template>
  <!-- One line of chips sized to the space the card actually has: as many as fit, then +N. Hovering
       +N opens a small card with every tag. No click handler anywhere: the whole chat card is the
       click target and a nested handler would swallow it. -->
  <div ref="row" class="v2026-ctr" :class="{ 'v2026-ctr--compact': compact }">

    <ActionChipV2026 v-if="archived" class="v2026-ctr-resolved" :color="'#007bff'" :icon="resolvedIcon">Resolved</ActionChipV2026>
    <ActionChipV2026 v-for="item in visibleItems" :key="item.key"
                     class="v2026-ctr-chip"
                     :color="item.color" :fill="item.fill" :icon="item.icon" :title="item.label">
      <span class="v2026-ctr-chip__t">{{ item.label }}</span>
    </ActionChipV2026>
    <span v-if="hiddenCount > 0" ref="more" class="v2026-ctr-more"
          @mouseenter="openPopover" @mouseleave="scheduleClose">+{{ hiddenCount }}</span>

    <!-- Measuring copy: every chip at its natural (clamped) width, never visible. -->
    <div ref="measure" class="v2026-ctr-measure" aria-hidden="true">
      <ActionChipV2026 v-if="archived" class="v2026-ctr-resolved" :color="'#007bff'" :icon="resolvedIcon">Resolved</ActionChipV2026>
      <ActionChipV2026 v-for="item in items" :key="'m' + item.key"
                       class="v2026-ctr-chip" data-measure-chip
                       :color="item.color" :fill="item.fill" :icon="item.icon">
        <span class="v2026-ctr-chip__t">{{ item.label }}</span>
      </ActionChipV2026>
      <span class="v2026-ctr-more" data-measure-more>+{{ items.length }}</span>
    </div>

    <!-- Moved to <body> on mount: the chat list scrolls, so anything positioned inside it is clipped. -->
    <div ref="popHost" class="v2026-ctr-pophost" :style="popStyle"
         @mouseenter="cancelClose" @mouseleave="scheduleClose">
      <OverlayCard v-if="popOpen" class="v2026-ctr-pop" :open="popOpen" :closeOnOutside="false">
        <div class="v2026-ctr-pop__title">แท็กทั้งหมด ({{ items.length }})</div>
        <div class="v2026-ctr-pop__list">
          <ActionChipV2026 v-for="item in items" :key="'p' + item.key"
                           class="v2026-ctr-chip v2026-ctr-chip--pop"
                           :color="item.color" :fill="item.fill" :icon="item.icon" :title="item.label">
            <span class="v2026-ctr-chip__t">{{ item.label }}</span>
          </ActionChipV2026>
        </div>
      </OverlayCard>
    </div>

  </div>
</template>

<script>
import ActionChipV2026 from '@/components/coversation/ActionChipV2026'
import OverlayCard from '@/components/coversation/OverlayCard.vue'

// Must equal .v2026-ctr's gap.
const CHIP_GAP = 8
// Grace period to move the pointer from +N into the popover without it closing.
const CLOSE_DELAY_MS = 120
// Space kept between the popover and the viewport edge.
const VIEWPORT_PAD = 8

export default {
  name: 'card-tag-row-v2026',
  components: {
    ActionChipV2026,
    OverlayCard
  },
  props: {
    // [{ key, label, color, fill, icon }] — ChatCardV2026.cardTagItems, in row order.
    items: {
      type: Array,
      default: () => []
    },
    // Resolved chip first. It is not a tag, so it never goes into +N or the popover.
    archived: {
      type: Boolean,
      default: false
    },
    resolvedIcon: {
      type: String,
      required: true
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      fitCount: 1, // corrected by the first measure()
      popOpen: false,
      popStyle: { top: '0px', left: '0px' },
      closeTimer: null,
      measureFrame: null,
      resizeObserver: null
    }
  },
  computed: {
    visibleItems () {
      return this.items.slice(0, this.fitCount)
    },
    hiddenCount () {
      return Math.max(0, this.items.length - this.fitCount)
    },
    // Re-measure only when something that changes a width changes.
    measureKey () {
      return (this.archived ? 'r|' : '') + this.compact + '|' + this.items.map(i => i.key + ':' + i.label).join('|')
    }
  },
  watch: {
    measureKey () {
      this.$nextTick(this.requestMeasure)
    }
  },
  mounted () {
    document.body.appendChild(this.$refs.popHost)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.requestMeasure)
      this.resizeObserver.observe(this.$refs.row)
    }
    this.$nextTick(this.requestMeasure)
  },
  beforeDestroy () {
    this.clearCloseTimer()
    if (this.measureFrame) cancelAnimationFrame(this.measureFrame)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    const host = this.$refs.popHost
    if (host && host.parentNode) host.parentNode.removeChild(host)
  },
  methods: {
    requestMeasure () {
      if (this.measureFrame) return
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null
        this.measure()
      })
    },
    measure () {
      const row = this.$refs.row
      const measure = this.$refs.measure
      if (!row || !measure) return
      const available = row.clientWidth
      if (!available) return
      const width = el => (el ? el.getBoundingClientRect().width : 0)
      const chipWidths = Array.from(measure.querySelectorAll('[data-measure-chip]')).map(width)
      const moreWidth = width(measure.querySelector('[data-measure-more]'))
      const resolvedWidth = this.archived ? width(measure.querySelector('.v2026-ctr-resolved')) : 0

      let used = resolvedWidth
      let placed = this.archived ? 1 : 0
      let fit = 0
      for (let i = 0; i < chipWidths.length; i++) {
        const lead = placed > 0 ? CHIP_GAP : 0
        const remaining = chipWidths.length - (i + 1)
        const reserve = remaining > 0 ? CHIP_GAP + moreWidth : 0
        if (used + lead + chipWidths[i] + reserve > available) break
        used += lead + chipWidths[i]
        placed++
        fit++
      }
      // Always show one tag (it shrinks with an ellipsis); an archived card may give that slot to Resolved.
      if (fit === 0 && !this.archived && chipWidths.length) fit = 1
      this.fitCount = fit
      if (!this.hiddenCount) this.closePopover()
    },
    openPopover () {
      this.cancelClose()
      const more = this.$refs.more
      if (!more) return
      const r = more.getBoundingClientRect()
      this.popStyle = { top: `${r.bottom + 6}px`, left: `${r.left}px` }
      this.popOpen = true
      // Second pass once the card has a size: keep it inside the viewport, flip above if needed.
      this.$nextTick(() => {
        const host = this.$refs.popHost
        const card = host && host.firstElementChild
        if (!card) return
        const c = card.getBoundingClientRect()
        let left = r.left
        let top = r.bottom + 6
        if (left + c.width > window.innerWidth - VIEWPORT_PAD) {
          left = Math.max(VIEWPORT_PAD, window.innerWidth - VIEWPORT_PAD - c.width)
        }
        if (top + c.height > window.innerHeight - VIEWPORT_PAD && r.top - 6 - c.height >= VIEWPORT_PAD) {
          top = r.top - 6 - c.height
        }
        this.popStyle = { top: `${top}px`, left: `${left}px` }
      })
    },
    scheduleClose () {
      this.clearCloseTimer()
      this.closeTimer = setTimeout(this.closePopover, CLOSE_DELAY_MS)
    },
    cancelClose () {
      this.clearCloseTimer()
    },
    closePopover () {
      this.clearCloseTimer()
      this.popOpen = false
    },
    clearCloseTimer () {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    }
  }
}
</script>

<style scoped>
.v2026-ctr {
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px; /* = CHIP_GAP */
  min-width: 0;
  overflow: hidden;
}

.v2026-ctr-resolved,
.v2026-ctr-more {
  flex: 0 0 auto;
}

/* Visible tag chips may shrink (ellipsis) — only the one forced chip ever needs to. */
.v2026-ctr-chip {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 120px;
}
.v2026-ctr--compact .v2026-ctr-chip {
  max-width: 100%;
}
.v2026-ctr--compact .v2026-ctr-chip,
.v2026-ctr--compact .v2026-ctr-resolved {
  padding: 5px 8px;
}

/* The glyph is a flex item and would shrink before the text does; pin it. */
::v-deep .v2026-ctr-chip .v2026-action-chip-ico {
  flex: 0 0 auto;
}

.v2026-ctr-chip__t {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A plain span, never an ActionChipV2026 (an empty mask url paints a solid square). line-height 18px
   reproduces a chip's 30px height. */
.v2026-ctr-more {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  line-height: 18px;
  color: #868e96;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  padding: 5px 10px;
  cursor: default;
}
.v2026-ctr-more:hover {
  color: #495057;
  border-color: #ced4da;
}

.v2026-ctr-measure {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  gap: 8px;
  width: max-content;
  visibility: hidden;
  pointer-events: none;
}
/* Measured unshrunk: only the max-width clamp may narrow a chip. */
.v2026-ctr-measure > * {
  flex: 0 0 auto;
}

.v2026-ctr-pophost {
  position: fixed;
  z-index: 1060;
  width: 0;
  height: 0;
}

/* The host is 0px wide, so without max-content the card collapses to min-width and stacks every chip. */
.v2026-ctr-pop {
  top: 0;
  left: 0;
  width: max-content;
  min-width: 200px;
  max-width: 300px;
  padding: 12px;
}
.v2026-ctr-pop__title {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #667085;
}
.v2026-ctr-pop__list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.v2026-ctr-chip--pop {
  max-width: 100%;
}
</style>
